import { motion } from "framer-motion";

import type { AnalyticsDeltas, AnalyticsSummary } from "@/lib/api";
import { cn } from "@/lib/formatters";
import { useCountUp } from "@/lib/useCountUp";

interface KPICardsProps {
  summary: AnalyticsSummary;
  deltas: AnalyticsDeltas;
}

type Hue = "cyan" | "magenta" | "violet" | "success";

interface KPI {
  label: string;
  value: number;
  formatter: (v: number) => string;
  delta: number;
  suffix: string;
  invert?: boolean;
  hue: Hue;
}

function fmtInt(n: number): string {
  return Math.round(n).toLocaleString("en-US");
}

function fmtPct(v: number): string {
  return `${(v * 100).toFixed(2)}%`;
}

function fmtDecimal(v: number): string {
  return v.toFixed(1);
}

export function KPICards({ summary, deltas }: KPICardsProps) {
  const kpis: KPI[] = [
    { label: "clicks",       value: summary.clicks,      formatter: fmtInt,     delta: deltas.delta.clicks,      suffix: "",     hue: "cyan"    },
    { label: "impressions",  value: summary.impressions, formatter: fmtInt,     delta: deltas.delta.impressions, suffix: "",     hue: "violet"  },
    { label: "ctr",          value: summary.ctr,         formatter: fmtPct,     delta: deltas.delta.ctr_pp,      suffix: " pp",  hue: "magenta" },
    { label: "avg position", value: summary.position,    formatter: fmtDecimal, delta: deltas.delta.position,    suffix: "",     hue: "success", invert: true },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {kpis.map((k, i) => (
        <KPICard key={k.label} kpi={k} index={i} windowDays={deltas.window_days} />
      ))}
    </div>
  );
}

function KPICard({ kpi, index, windowDays }: { kpi: KPI; index: number; windowDays: number }) {
  const animated = useCountUp(kpi.value);
  const positive = kpi.invert ? kpi.delta < 0 : kpi.delta > 0;

  const numClass =
    kpi.hue === "magenta" ? "num-hero-magenta text-magenta"
    : kpi.hue === "success" ? "num-hero-success text-success"
    : kpi.hue === "cyan" ? "num-hero text-primary"
    : "num-hero text-violet";

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.06, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="surface surface-hoverable p-4 relative overflow-hidden"
    >
      {/* corner LED */}
      <span
        className={cn(
          "absolute top-3 right-3 w-1.5 h-1.5 rounded-full animate-pulse-led",
          positive ? "bg-success" : kpi.delta === 0 ? "bg-muted" : "bg-danger",
        )}
      />

      <p className="path-chip">{kpi.label}</p>
      <p className={cn("text-3xl font-mono mt-2", numClass)}>
        {kpi.formatter(animated)}
      </p>
      <DeltaBadge
        delta={kpi.delta}
        suffix={kpi.suffix}
        invert={kpi.invert}
        windowDays={windowDays}
      />
    </motion.div>
  );
}

function DeltaBadge({
  delta,
  suffix,
  invert,
  windowDays,
}: {
  delta: number;
  suffix: string;
  invert?: boolean;
  windowDays: number;
}) {
  if (delta === 0) {
    return (
      <p className="mt-1 text-xs font-mono text-muted">
        = 0{suffix} vs prev {windowDays}d
      </p>
    );
  }
  const positive = invert ? delta < 0 : delta > 0;
  const arrow = delta > 0 ? "▲" : "▼";
  const color = positive ? "text-success" : "text-danger";
  const absDelta = Math.abs(delta);
  const displayValue = suffix === " pp"
    ? absDelta.toFixed(2)
    : Number.isInteger(delta) ? absDelta.toString() : absDelta.toFixed(1);

  return (
    <p className={cn("mt-1 text-xs font-mono", color)}>
      {arrow} {delta > 0 ? "+" : "-"}{displayValue}{suffix}
      <span className="text-muted"> vs prev {windowDays}d</span>
    </p>
  );
}
